/**
 * useAutoImproveFlags — reads the self-healing runtime-flags feed and
 * makes them available to any React component. The auto-improve engine
 * writes flags like:
 *
 *   suppress:/community        → hide/replace the broken route
 *   roadmap_tease:<bucket>     → surface an "in progress" hint
 *   backup_mode:<endpoint>     → skip primary path, go straight to fallback
 *
 * Consumers:
 *   const flags = useAutoImproveFlags();
 *   const isSuppressed = flags[`suppress:${location.pathname}`]?.value;
 *
 * Poll every 60 s so newly-applied fixes propagate without a page reload.
 */
import { useEffect, useState } from "react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
let _cache = null;
let _cacheAt = 0;
const _listeners = new Set();

async function _refresh() {
  try {
    const r = await fetch(`${API}/api/auto-improve/runtime-flags`, { cache: "no-store" });
    if (!r.ok) return;
    const d = await r.json();
    _cache = d.flags || {};
    _cacheAt = Date.now();
    _listeners.forEach((fn) => { try { fn(_cache); } catch (_) {} });
  } catch (_e) { /* offline / cold-start — keep cache */ }
}

// Kick off a background poll shared across all subscribers.
let _pollTimer = null;
function _startPoll() {
  if (_pollTimer) return;
  _refresh();
  _pollTimer = setInterval(_refresh, 60_000);
}

export function useAutoImproveFlags() {
  const [flags, setFlags] = useState(_cache || {});
  useEffect(() => {
    _startPoll();
    _listeners.add(setFlags);
    if (_cache && Date.now() - _cacheAt < 60_000) setFlags(_cache);
    return () => { _listeners.delete(setFlags); };
  }, []);
  return flags;
}

export function isPathSuppressed(pathname, flags) {
  if (!flags || !pathname) return false;
  return !!flags[`suppress:${pathname}`]?.value;
}
